import { useQueryClient } from '@tanstack/react-query'
import {
  PowerOff,
  RefreshCw,
} from 'lucide-react'
import { useState } from 'react'

import { ConfirmationDialog } from 'components/reusable/ConfirmationDialog'
import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import {
  getGetBrokersMembersMemberIdFinancialAccountsQueryKey,
  usePostBrokersMembersMemberIdFinancialAccountsFinancialAccountIdDeactivate,
  usePostBrokersMembersMemberIdFinancialAccountsFinancialAccountIdSyncStatus,
} from 'utils/web_api'
import { type BrokersFinancialAccount } from 'utils/web_api.schemas'

type FinancialAccountActionsProps = {
  memberId: string
  account: BrokersFinancialAccount
}

const DeactivateButton = ({
  account,
  memberId,
  updateAccountInCache,
}: {
  account: BrokersFinancialAccount
  memberId: string
  updateAccountInCache: (updatedAccount: BrokersFinancialAccount) => void
}) => {
  const [showDeactivateDialog, setShowDeactivateDialog] = useState<boolean>(false)

  const deactivateMutation =
    usePostBrokersMembersMemberIdFinancialAccountsFinancialAccountIdDeactivate({
      mutation: {
        onSuccess: (data) => {
          if (data) {
            updateAccountInCache(data)
          }
        },
      },
    })

  const handleDeactivate = async () => {
    try {
      await deactivateMutation.mutateAsync({
        memberId,
        financialAccountId: account.id,
      })
    } catch (error) {
      console.error('Failed to deactivate financial account:', error)
    }
  }

  if (account.status !== 'active') {
    return null
  }

  const maskedAccountNumber = account.masked_account_number || account.id.slice(-6)

  return (
    <>
      <DisplayTooltipOnHover text='Deactivate VBAN'>
        <div className='cursor-pointer h-full' onClick={() => setShowDeactivateDialog(true)}>
          <PowerOff
            size={20}
            className={`stroke-current text-red-600 ${deactivateMutation.isPending ? 'opacity-50' : ''}`}
          />
        </div>
      </DisplayTooltipOnHover>
      <ConfirmationDialog
        open={showDeactivateDialog}
        onOpenChange={setShowDeactivateDialog}
        title='Deactivate VBAN'
        description={`Are you sure you want to deactivate the VBAN account ending in ${maskedAccountNumber}? This action cannot be undone.`}
        submitLabel='Deactivate'
        onSubmit={handleDeactivate}
        isSubmitting={deactivateMutation.isPending}
        isDanger
      />
    </>
  )
}

const FinancialAccountActions = ({
  memberId,
  account,
}: FinancialAccountActionsProps) => {
  const queryClient = useQueryClient()

  const queryKey = getGetBrokersMembersMemberIdFinancialAccountsQueryKey(memberId)

  const updateAccountInCache = (updatedAccount: BrokersFinancialAccount) => {
    queryClient.setQueryData(queryKey, (oldData: BrokersFinancialAccount[] | undefined) => {
      if (!oldData) return oldData

      return oldData.map((existing: BrokersFinancialAccount) =>
        existing.id === updatedAccount.id ? updatedAccount : existing
      )
    })
  }

  const syncStatusMutation =
    usePostBrokersMembersMemberIdFinancialAccountsFinancialAccountIdSyncStatus({
      mutation: {
        onSuccess: (data) => {
          if (data) {
            updateAccountInCache(data)
          }
        },
      },
    })

  const handleSyncStatus = async () => {
    try {
      await syncStatusMutation.mutateAsync({
        memberId,
        financialAccountId: account.id,
      })
    } catch (error) {
      console.error('Failed to sync financial account status:', error)
    }
  }

  return (
    <div className='flex items-center gap-4'>
      <DisplayTooltipOnHover text='Sync status with provider'>
        <div className='cursor-pointer h-full' onClick={handleSyncStatus}>
          <RefreshCw
            size={20}
            className={`stroke-current text-gray-600 ${
              syncStatusMutation.isPending ? 'animate-spin' : ''
            }`}
          />
        </div>
      </DisplayTooltipOnHover>

      <DeactivateButton
        account={account}
        memberId={memberId}
        updateAccountInCache={updateAccountInCache}
      />
    </div>
  )
}

export default FinancialAccountActions
